/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useMemo } from 'react';
import ContentLoader, { IContentLoaderProps } from 'react-content-loader';

import { skeletonConstants } from '../constants/skeletonConstants';
import { wrapSkeleton, WrapSkeletonInjectedProps } from './helpers/wrapSkeleton';

type RowSkeletonProps = IContentLoaderProps &
  WrapSkeletonInjectedProps & {
    borderRadius?: number;
  };

const RowSkeletonComponent = memo<RowSkeletonProps>(props => {
  const {
    height,
    width,
    borderRadius = skeletonConstants.loaderBorderRadius,
    skeletonWrapper,
    style = {},
    ...otherProps
  } = props;
  const { wWidth = 56, wHeight = 12 } = skeletonWrapper;

  const totalWidth = useMemo(() => width ?? wWidth, [wWidth, width]);
  const totalHeight = useMemo(() => height ?? wHeight, [height, wHeight]);

  return (
    <ContentLoader
      height={totalHeight}
      width={totalWidth}
      backgroundColor={skeletonConstants.backgroundColor}
      foregroundColor={skeletonConstants.foregroundColor}
      style={{
        backgroundColor: skeletonConstants.loaderBackgroundColor,
        borderRadius: borderRadius,
        ...style,
        height: `${totalHeight}px`,
        width: `${totalWidth}px`,
      }}
      preserveAspectRatio="xMinYMin slice"
      {...otherProps}
    >
      <rect x="0" y="0" width="100%" height="100%" rx={borderRadius} ry={borderRadius} />
    </ContentLoader>
  );
});

export const RowSkeleton = wrapSkeleton(RowSkeletonComponent, 'row-skeleton');
